import { z } from 'zod';
import { IsoDateTime, Uuid, patchOf } from './common';

const Centavos = z.number().int().min(0).max(100_000_000);
const Quantity = z
  .number()
  .min(0)
  .max(1_000_000)
  .refine((v) => Math.abs(v * 1000 - Math.round(v * 1000)) < 1e-6, 'Use at most 3 decimals');

export const SupplyCategory = z.enum(['hardware', 'finishing', 'packaging']);
export type SupplyCategory = z.infer<typeof SupplyCategory>;
export const SUPPLY_CATEGORY_LABELS: Record<SupplyCategory, string> = {
  hardware: 'Ferragens',
  finishing: 'Acabamento',
  packaging: 'Embalagem',
};
export const SupplyUnit = z.enum(['unit', 'meter', 'turn']);
export type SupplyUnit = z.infer<typeof SupplyUnit>;
export const SUPPLY_UNIT_LABELS: Record<SupplyUnit, { one: string; many: string; short: string }> =
  {
    unit: { one: 'unidade', many: 'unidades', short: 'un.' },
    meter: { one: 'metro', many: 'metros', short: 'm' },
    turn: { one: 'volta', many: 'voltas', short: 'volta' },
  };

const SupplyFields = z.object({
  name: z.string().trim().min(1).max(120),
  category: SupplyCategory,
  unit: SupplyUnit.default('unit'),
  unitCostCentavos: Centavos.describe('Cost per unit (from the last purchase).'),
  minimumQuantity: Quantity.nullable()
    .default(null)
    .describe('Low-stock threshold (used with the inventory).'),
  externalRef: z.string().trim().max(200).nullable().default(null),
});
export const CreateSupplyBody = SupplyFields.extend({
  quantityOnHand: Quantity.default(0).describe('Stock counted now ("Estoque atual").'),
}).meta({
  example: { name: 'Ímã 10 × 2 mm', category: 'hardware', unit: 'unit', unitCostCentavos: 35 },
});
export type CreateSupplyBody = z.input<typeof CreateSupplyBody>;
export const UpdateSupplyBody = patchOf(SupplyFields);
export type UpdateSupplyBody = z.input<typeof UpdateSupplyBody>;

export const Supply = z
  .object({
    id: Uuid,
    name: z.string(),
    category: SupplyCategory,
    unit: SupplyUnit,
    unitCostCentavos: z.number().int(),
    minimumQuantity: z.number().nullable(),
    quantityOnHand: z.number(),
    low: z.boolean().describe('Has a minimum and the stock is below it.'),
    usedInKits: z.array(z.object({ id: Uuid, name: z.string() })),
    externalRef: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Supply' });
export type Supply = z.infer<typeof Supply>;
export const SupplyList = z.object({ data: z.array(Supply), nextCursor: z.string().nullable() });
export const SupplyQuery = z.object({
  category: SupplyCategory.optional(),
  low: z.enum(['true', 'false']).optional().describe('Only supplies below their minimum.'),
  q: z.string().trim().max(100).optional(),
});

const KitFields = z.object({
  name: z.string().trim().min(1).max(120),
  items: z
    .array(
      z.object({
        supplyId: Uuid,
        quantity: Quantity.refine((v) => v > 0, 'Quantity must be positive'),
      }),
    )
    .max(50)
    .default([])
    .describe('Supplies per shipment.'),
  extraLines: z
    .array(z.object({ name: z.string().trim().min(1).max(120), costCentavos: Centavos }))
    .max(20)
    .default([])
    .describe('Items outside the stock (e.g. kraft paper filling).'),
});
export const CreateKitBody = KitFields;
export type CreateKitBody = z.input<typeof CreateKitBody>;
export const UpdateKitBody = patchOf(KitFields);
export type UpdateKitBody = z.input<typeof UpdateKitBody>;

export const PackagingKit = z
  .object({
    id: Uuid,
    name: z.string(),
    items: z.array(
      z.object({
        supplyId: Uuid,
        name: z.string(),
        unit: SupplyUnit,
        unitCostCentavos: z.number().int(),
        quantity: z.number(),
        subtotalCentavos: z.number().int(),
      }),
    ),
    extraLines: z.array(z.object({ name: z.string(), costCentavos: z.number().int() })),
    totalCentavos: z
      .number()
      .int()
      .describe('Kit cost per shipment: items × current unit cost + extras.'),
    shipmentsPossible: z
      .number()
      .int()
      .nullable()
      .describe('How many shipments the stock covers (null when the kit has no counted items).'),
    lowItems: z.array(z.string()).describe('Kit items below their minimum.'),
    defaultFor: z
      .array(z.object({ id: Uuid, name: z.string() }))
      .describe('Channels using it as default kit.'),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'PackagingKit' });
export type PackagingKit = z.infer<typeof PackagingKit>;
export const PackagingKitList = z.object({
  data: z.array(PackagingKit),
  nextCursor: z.string().nullable(),
});
